import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import {
    PageData,
    ReadonlyRepository,
} from '@homeleite/arch';
import { BehaviorSubject, lastValueFrom, Subject, timer } from 'rxjs';
import { debounce, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { EntityListPerspective } from '../entity-list-perspective/entity-list-perspective';
import { LoadQueue } from '../shared/load-helper';
import { EntityCardDirective } from './entity-card.directive';

interface QueryData {
    summary: boolean;
    searchTerm: string;
    perspective: EntityListPerspective;
}
const EMPTY: QueryData = {
    summary: true,
    searchTerm: '',
    perspective: null,
};

const DEFAULT_PAGE_SIZE = 20;
/** Evita loop infinito se o count vier inconsistente. */
const FILL_MAX_PAGES = 100;

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'entity-cards',
    templateUrl: './entity-cards.component.html',
    styleUrls: ['./entity-cards.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityCardsComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() repository: ReadonlyRepository<any, any>;
    @Input() lazyLoading = true;

    /**
     * Preferir `false`: o scroll real fica em `.cards` (desktop e mobile).
     * `true` só quando a página inteira (window) é o scroller — e nesse caso o
     * sentinel/IntersectionObserver ainda cobre o carregamento.
     */
    @Input() infiniteScrollWindow = false;

    @Output() entitySelect = new EventEmitter<any>();
    /** Mantido para compatibilidade; preferir `loadError$` no template. */
    @Output() loadFail = new EventEmitter<Error | null>();
    @Output() isEmpty = new EventEmitter<boolean>();

    @ViewChild('cards') cards: ElementRef<HTMLElement>;
    @ViewChild('scrollSentinel') scrollSentinel: ElementRef<HTMLElement>;

    @ContentChild(EntityCardDirective, { static: false })
    cardTemplate: EntityCardDirective<any>;

    filteredData: any[] = [];

    filteredTotal = 0;
    loadQueue = new LoadQueue();

    /** Estado de erro estável (EventEmitter + async pipe falhava com OnPush). */
    readonly loadError$ = new BehaviorSubject<any>(null);

    private currentPage = new PageData(0, DEFAULT_PAGE_SIZE);

    private destroy$ = new Subject<void>();

    private loadSubject = new BehaviorSubject<QueryData>(EMPTY);

    /** Observa o sentinel no viewport (mobile/window) e no container `.cards`. */
    private viewportObserver?: IntersectionObserver;
    private cardsObserver?: IntersectionObserver;
    private fillScheduled = false;

    constructor(
        private changeDetector: ChangeDetectorRef
    ) { }

    get loading() {
        return !this.loadQueue.empty;
    }

    get isFiltered() {
        return this.searchTerm && this.searchTerm.trim() !== '';
    }

    get summary() {
        return this.loadSubject.value.summary;
    }

    get searchTerm(): string {
        return this.loadSubject.value.searchTerm;
    }

    /** Antes da 1ª carga terminar não existe "lista vazia" — evita piscar "Nenhum registro". */
    loadedOnce = false;

    get noRegister(): boolean {
        const noResults =
            this.loadedOnce &&
            !this.loading &&
            this.filteredData.length === 0 &&
            !this.loadError$.value;
        noResults ? this.isEmpty.emit(true) : this.isEmpty.emit(false);
        return noResults;
    }

    /**
     * Ainda há páginas a carregar. Usado pelo botão "Carregar mais" (fallback
     * mobile/desktop quando scroll/IO não dispara) e pelo preenchimento automático.
     */
    get canLoadMore(): boolean {
        if (!this.lazyLoading || this.loading || !this.loadedOnce) {
            return false;
        }
        if (this.filteredTotal > 0) {
            return this.filteredData.length < this.filteredTotal;
        }
        return this.filteredData.length > 0 &&
            this.filteredData.length >= this.currentPage.rows;
    }

    get errorMessage(): string {
        const e = this.loadError$.value;
        if (!e) {
            return '';
        }
        const status = e.status ?? e?.error?.code;
        if (status === 401 || status === 403) {
            return 'Sessão expirada ou sem permissão. Entre novamente.';
        }
        if (status === 0 || e.name === 'TimeoutError') {
            return 'Sem conexão com o servidor. Verifique a rede e tente de novo.';
        }
        if (status >= 500) {
            return `Servidor indisponível (HTTP ${status}).`;
        }
        if (status) {
            return `Falha ao buscar os registros (HTTP ${status}).`;
        }
        return 'Falha ao buscar os registros.';
    }

    @Input()
    set summary(value: boolean) {
        if (this.summary !== value) {
            this.loadSubject.next({
                summary: value,
                perspective: this.perspective,
                searchTerm: this.searchTerm,
            });
        }
    }

    get perspective() {
        return this.loadSubject.value.perspective;
    }

    @Input()
    set perspective(value: EntityListPerspective) {
        if (!this.perspective || !this.perspective.isEqual(value)) {
            this.loadSubject.next({
                summary: this.summary,
                perspective: value,
                searchTerm: this.searchTerm,
            });
        }
    }

    @Input()
    set searchTerm(value: string) {
        if (this.searchTerm !== value) {
            this.loadSubject.next({
                summary: this.summary,
                perspective: this.perspective,
                searchTerm: value,
            });
        }
    }

    ngOnInit(): void {
        // no-op: estado de erro via loadError$
    }

    ngAfterViewInit() {
        this.loadSubject
            .pipe(
                filter((v) => Boolean(v.perspective) && Boolean(this.repository)),
                // 1ª carga sem espera; debounce curto (antes 300ms) — troca de categoria
                // com 10k animais ficava "lenta" so de esperar o debounce.
                debounce(() => timer(this.loadedOnce ? 40 : 0)),
                distinctUntilChanged(
                    (v1, v2) =>
                        v1.searchTerm === v2.searchTerm &&
                        v1.summary === v2.summary &&
                        (v1.perspective === v2.perspective ||
                            v1.perspective.isEqual(v2.perspective))
                ),
                takeUntil(this.destroy$)
            )
            .subscribe((data) => {
                this.changeDetector.markForCheck();
                this.refresh(data);
            });
    }

    ngOnDestroy() {
        this.teardownObservers();
        this.destroy$.next();
        this.destroy$.complete();
        this.loadError$.complete();
        this.filteredData = [];
    }

    onEntityClick(event: any) {
        this.entitySelect.emit(event.row);
    }

    search(searchTerm: string): void {
        this.searchTerm = searchTerm;
    }

    invalidate() {
        this.refresh();
    }

    /**
     * Recarrega o que já está na tela (mesma quantidade, sem rolar ao topo nem esvaziar).
     * Para dados que mudaram por trás; com cache em memória não há espera perceptível.
     */
    async softRefresh(): Promise<void> {
        const v = this.loadSubject.value;
        if (!v.perspective || !this.repository || !this.loadedOnce || !this.lazyLoading) {
            return this.refresh();
        }
        const rows = Math.max(this.filteredData.length, DEFAULT_PAGE_SIZE);
        try {
            const data = await this.fetchPage(new PageData(0, rows), v);
            this.currentPage = new PageData(rows - DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE);
            this.filteredData = data;
            const total = await lastValueFrom(
                this.repository.count(v.searchTerm, v.perspective.filters, v.perspective.actives)
            );
            this.filteredTotal = total;
            this.loadError$.next(null);
        } catch {
            // mantém o que já está na tela
        }
        this.changeDetector.markForCheck();
        this.scheduleFillAndObserve();
    }

    trackByFn(entity: any) {
        return entity.id || entity;
    }

    onScrollDown(_event?: any) {
        void this.loadNextPage();
    }

    async loadNextPage(): Promise<void> {
        const loaded = await this.fetchNextPage();
        if (loaded) {
            this.scheduleFillAndObserve();
        } else {
            this.reconnectObservers();
        }
    }

    /** Só busca a próxima página; sem agendar fill (usado pelo loop de preenchimento). */
    private async fetchNextPage(): Promise<boolean> {
        if (this.loading) {
            return false;
        }
        if (
            this.filteredTotal > 0 &&
            this.filteredData.length >= this.filteredTotal
        ) {
            return false;
        }
        const before = this.filteredData.length;
        await this.runNetworkOp(async () => {
            const more = await this.fetchPage(
                this.currentPage.next,
                this.loadSubject.value
            );
            if (!more.length) {
                this.filteredTotal = this.filteredData.length;
                return;
            }
            this.filteredData = [...this.filteredData, ...more];
            if (more.length < this.currentPage.rows) {
                this.filteredTotal = this.filteredData.length;
            }
        });
        return this.filteredData.length > before;
    }

    async refresh(v: QueryData = this.loadSubject.value) {
        this.changeDetector.markForCheck();
        await this.runNetworkOp(async () => {
            if (this.cards?.nativeElement) {
                this.cards.nativeElement.scrollTo(0, 0);
            }

            // Pinta a lista assim que a página chega; count em paralelo não bloqueia
            const data = this.lazyLoading
                ? await this.fetchPage(new PageData(0, this.currentPage.rows), v)
                : await this.fetch(v);

            this.filteredData = data;
            this.filteredTotal = data.length;
            this.loadedOnce = true;
            this.changeDetector.markForCheck();

            try {
                const total = await lastValueFrom(
                    this.repository.count(
                        v.searchTerm,
                        v.perspective.filters,
                        v.perspective.actives
                    )
                );
                this.filteredTotal = total;
            } catch {
                // total indisponível — lista já visível
            }
        });
        this.scheduleFillAndObserve();
    }

    private async fetch(v: QueryData = this.loadSubject.value): Promise<any[]> {
        return await lastValueFrom(
            v.summary
                ? this.repository.list(
                    v.searchTerm,
                    v.perspective.filters,
                    v.perspective.sorts,
                    v.perspective.actives
                )
                : this.repository.listFull(
                    v.searchTerm,
                    v.perspective.filters,
                    v.perspective.sorts,
                    v.perspective.actives
                )
        );
    }

    private async fetchPage(
        page: PageData,
        v: QueryData = this.loadSubject.value
    ): Promise<any[]> {
        const result = await lastValueFrom(
            v.summary
                ? this.repository.page(
                    page,
                    v.searchTerm,
                    v.perspective.filters,
                    v.perspective.sorts,
                    v.perspective.actives
                )
                : this.repository.pageFull(
                    page,
                    v.searchTerm,
                    v.perspective.filters,
                    v.perspective.sorts,
                    v.perspective.actives
                )
        );
        this.currentPage = page;
        return result;
    }

    private async runNetworkOp<T>(action: () => Promise<T>): Promise<T> {
        const load = this.loadQueue.enqueue();
        this.changeDetector.markForCheck();
        try {
            const result = await action();
            this.loadError$.next(null);
            this.loadFail.emit(null);
            return result;
        } catch (e) {
            this.filteredTotal = 0;
            this.filteredData = [];
            this.loadedOnce = true;
            this.loadError$.next(e);
            this.loadFail.emit(e as Error);
            console.error(e);
        } finally {
            this.loadQueue.markFinished(load);
            this.changeDetector.markForCheck();
        }
    }

    /**
     * Depois de pintar a 1ª página: se ainda cabe na tela (comum no mobile
     * com poucos cards / tela alta), carrega páginas até haver scroll ou acabar.
     * Depois (re)liga IntersectionObserver no sentinel.
     */
    private scheduleFillAndObserve(): void {
        if (this.fillScheduled || !this.lazyLoading) {
            return;
        }
        this.fillScheduled = true;
        // Espera o DOM pintar cards + sentinel (OnPush + mobile layout).
        queueMicrotask(() => {
            requestAnimationFrame(() => {
                void this.fillUntilScrollable().finally(() => {
                    this.fillScheduled = false;
                    this.reconnectObservers();
                    this.changeDetector.markForCheck();
                });
            });
        });
    }

    /**
     * Preenche até o container `.cards` ou o viewport precisarem de scroll.
     * Cobre o caso clássico: 20 cards, contador "82 de 82", espaço vazio embaixo
     * e infinite-scroll nunca dispara (nem no mobile com toque).
     */
    private async fillUntilScrollable(): Promise<void> {
        let pages = 0;
        while (pages < FILL_MAX_PAGES) {
            // canLoadMore exige !loading; após fetchNextPage o estado já liberou.
            if (!this.canLoadMore || !this.needsMoreContent()) {
                break;
            }
            const loaded = await this.fetchNextPage();
            pages += 1;
            if (!loaded) {
                break;
            }
        }
    }

    private needsMoreContent(): boolean {
        const cardsEl = this.cards?.nativeElement;
        if (cardsEl) {
            // Container com altura limitada e ainda sem overflow → precisa mais itens.
            if (cardsEl.clientHeight > 0 &&
                cardsEl.scrollHeight <= cardsEl.clientHeight + 24) {
                return true;
            }
        }
        const sentinel = this.scrollSentinel?.nativeElement;
        if (sentinel) {
            const rect = sentinel.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            // Sentinel ainda visível (ou quase) no viewport → usuário vê o fim cedo.
            if (rect.top < vh + 160) {
                return true;
            }
        }
        return false;
    }

    private reconnectObservers(): void {
        this.teardownObservers();
        if (!this.lazyLoading || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const sentinel = this.scrollSentinel?.nativeElement;
        if (!sentinel) {
            // Template ainda não renderizou o footer — tenta de novo no próximo frame.
            requestAnimationFrame(() => {
                if (this.scrollSentinel?.nativeElement && this.canLoadMore) {
                    this.reconnectObservers();
                }
            });
            return;
        }

        const onIntersect: IntersectionObserverCallback = (entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                void this.loadNextPage();
            }
        };

        // Viewport: cobre scroll da página / body (Safari iOS, Android Chrome).
        this.viewportObserver = new IntersectionObserver(onIntersect, {
            root: null,
            rootMargin: '180px 0px',
            threshold: 0,
        });
        this.viewportObserver.observe(sentinel);

        // Container `.cards`: cobre scroll interno com toque no mobile/desktop.
        const cardsEl = this.cards?.nativeElement;
        if (cardsEl) {
            this.cardsObserver = new IntersectionObserver(onIntersect, {
                root: cardsEl,
                rootMargin: '180px 0px',
                threshold: 0,
            });
            this.cardsObserver.observe(sentinel);
        }
    }

    private teardownObservers(): void {
        this.viewportObserver?.disconnect();
        this.cardsObserver?.disconnect();
        this.viewportObserver = undefined;
        this.cardsObserver = undefined;
    }
}
